import React, { useState } from 'react';
import styled from 'styled-components';
import { AlertTriangle, CalendarOff } from 'lucide-react';
import { formatCurrency, formatNumber } from '@kardume/services';
import type {
  ExperienceStatus,
  MerchantExperienceDetailDto,
  MerchantExperienceSessionDto,
  Tone,
} from '@kardume/types';
import { EXPERIENCE_STATUS_LABELS, EXPERIENCE_TYPE_LABELS } from '@kardume/types';
import { Badge, Drawer, Tabs } from '@kardume/ui';
import DataState from '../DataState';
import ExperienceImagesPanel from './ExperienceImagesPanel';
import ExperienceSessionsPanel from './ExperienceSessionsPanel';

/**
 * O detalhe de uma experiência: fotos e agenda em abas (card #347).
 *
 * O cabeçalho carrega os dois avisos que mudam o que o lojista faz a seguir:
 * o motivo da recusa (com o efeito de editar) e o "sem data futura", que é o
 * caso em que uma experiência aprovada não está em lugar nenhum.
 */

const Head = styled.div`
  display: grid;
  gap: 0.6rem;
  margin-bottom: 1rem;

  h3 {
    margin: 0;
    font-size: 1.05rem;
    color: ${(p) => p.theme.textPrimary};
  }
`;

const Meta = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  align-items: center;
  font-size: 0.82rem;
  color: ${(p) => p.theme.textSecondary};
`;

const Alert = styled.div<{ $tone: 'warning' | 'danger' }>`
  display: flex;
  gap: 0.55rem;
  align-items: flex-start;
  padding: 0.75rem 0.9rem;
  border: 1px solid ${(p) => (p.$tone === 'danger' ? p.theme.error : p.theme.warning)};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => (p.$tone === 'danger' ? p.theme.error : p.theme.warning)}14;
  font-size: 0.84rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  svg {
    flex-shrink: 0;
    margin-top: 0.1rem;
    color: ${(p) => (p.$tone === 'danger' ? p.theme.error : p.theme.warning)};
  }
  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const TabBody = styled.div`
  margin-top: 1rem;
`;

export type ExperienceDetailTab = 'images' | 'sessions';

function statusTone(status: ExperienceStatus): Tone {
  if (status === 'ACTIVE') return 'success';
  if (status === 'PENDING_REVIEW' || status === 'PAUSED' || status === 'DRAFT') return 'warning';
  if (status === 'REJECTED') return 'danger';
  return 'secondary';
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  detail: MerchantExperienceDetailDto | null;
  loading: boolean;
  error: string | null;
  canManage: boolean;
  onReload: () => Promise<void> | void;
  /** Card #348 - lista de participantes de uma data. */
  onOpenParticipants?: (session: MerchantExperienceSessionDto) => void;
}

const ExperienceDetailDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  detail,
  loading,
  error,
  canManage,
  onReload,
  onOpenParticipants,
}) => {
  const [tab, setTab] = useState<ExperienceDetailTab>('sessions');
  const experience = detail?.experience;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Experiência"
      width="860px"
      headerActions={
        experience && (
          <Badge tone={statusTone(experience.status)}>
            {EXPERIENCE_STATUS_LABELS[experience.status]}
          </Badge>
        )
      }
    >
      <DataState
        loading={loading}
        error={error}
        isEmpty={!detail}
        emptyTitle="Experiência não encontrada"
        onRetry={onReload}
      >
        {detail && experience && (
          <>
            <Head>
              <h3>{experience.title}</h3>
              <Meta>
                <Badge tone="secondary">{EXPERIENCE_TYPE_LABELS[experience.type]}</Badge>
                {experience.categoryName && <span>{experience.categoryName}</span>}
                <span>·</span>
                <span>{experience.free ? 'Gratuita' : formatCurrency(experience.price)}</span>
                <span>·</span>
                <span>{formatNumber(experience.upcomingSessions)} datas futuras</span>
                {experience.kardumeExclusive && <Badge tone="info">Exclusiva Kardume</Badge>}
                {experience.requiredPassPlanName && (
                  <Badge tone="primary">Pass {experience.requiredPassPlanName}</Badge>
                )}
              </Meta>

              {experience.status === 'REJECTED' && (
                <Alert $tone="danger">
                  <AlertTriangle size={16} />
                  <span>
                    <strong>Recusada pela moderação.</strong>{' '}
                    {experience.rejectionReason || 'Sem motivo registrado.'} Corrija o que foi
                    apontado: ao salvar a edição, a experiência volta automaticamente para a fila de
                    revisão.
                  </span>
                </Alert>
              )}

              {experience.status === 'PENDING_REVIEW' && (
                <Alert $tone="warning">
                  <AlertTriangle size={16} />
                  <span>
                    <strong>Aguardando revisão do Kardume.</strong> Ela ainda não aparece na vitrine.
                    Você já pode abrir as datas - assim, no momento em que for aprovada, já estará
                    visível.
                  </span>
                </Alert>
              )}

              {experience.status === 'ACTIVE' && experience.upcomingSessions === 0 && (
                <Alert $tone="warning">
                  <CalendarOff size={16} />
                  <span>
                    <strong>Aprovada, mas sem data futura.</strong> A vitrine só mostra experiências
                    com data marcada - abra uma data na aba Agenda para ela voltar a aparecer.
                  </span>
                </Alert>
              )}
            </Head>

            <Tabs
              value={tab}
              onChange={setTab}
              ariaLabel="Seções da experiência"
              idPrefix="experience-detail"
              tabs={[
                { value: 'sessions', label: `Agenda (${detail.sessions.length})` },
                { value: 'images', label: `Fotos (${detail.images.length})` },
              ]}
            />

            <TabBody>
              {tab === 'sessions' ? (
                <ExperienceSessionsPanel
                  experienceId={experience.id}
                  sessions={detail.sessions}
                  canManage={canManage}
                  onChanged={onReload}
                  onOpenParticipants={onOpenParticipants}
                />
              ) : (
                <ExperienceImagesPanel
                  experienceId={experience.id}
                  images={detail.images}
                  canManage={canManage}
                  onChanged={onReload}
                />
              )}
            </TabBody>
          </>
        )}
      </DataState>
    </Drawer>
  );
};

export default ExperienceDetailDrawer;
